import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import PageHeader from "@/components/PageHeader";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  api,
  type DmRule,
  type DmSend,
  type DmTemplate,
} from "@/lib/api";
import { cn, relativeTime } from "@/lib/utils";
import { MessageSquareWarning, RefreshCw, ShieldOff, TestTube2 } from "lucide-react";
import { useMemo, useState } from "react";

function FlagToggle({
  checked,
  onChange,
  label,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  label: string;
}) {
  return (
    <label className="flex cursor-pointer items-center gap-2 text-sm">
      <input
        type="checkbox"
        className="h-4 w-4"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        aria-label={label}
      />
      <span className="sr-only">{label}</span>
    </label>
  );
}

function statusBadge(status: string) {
  if (status === "sent") return "success" as const;
  if (status === "dry_run") return "secondary" as const;
  if (status === "error") return "destructive" as const;
  return "outline" as const;
}

export default function AutomatyzacjeDm() {
  const qc = useQueryClient();
  const statusQ = useQuery({ queryKey: ["dm-status"], queryFn: api.dmStatus, refetchInterval: 15_000 });
  const rulesQ = useQuery({ queryKey: ["dm-rules"], queryFn: api.dmRules });
  const templatesQ = useQuery({ queryKey: ["dm-templates"], queryFn: api.dmTemplates });
  const sendsQ = useQuery({ queryKey: ["dm-sends"], queryFn: () => api.dmSends(40), refetchInterval: 10_000 });

  const [testCommentId, setTestCommentId] = useState(`test_${Date.now()}`);
  const [testText, setTestText] = useState("mama");
  const [testPlatform, setTestPlatform] = useState<"fb" | "ig">("fb");
  const [editRule, setEditRule] = useState<DmRule | null>(null);
  const [editTemplate, setEditTemplate] = useState<DmTemplate | null>(null);
  const [keywordsDraft, setKeywordsDraft] = useState("");
  const [buttonsDraft, setButtonsDraft] = useState("");

  const templatesById = useMemo(() => {
    const map = new Map<string, DmTemplate>();
    for (const t of templatesQ.data?.items || []) map.set(t.id, t);
    return map;
  }, [templatesQ.data]);

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ["dm-status"] });
    void qc.invalidateQueries({ queryKey: ["dm-rules"] });
    void qc.invalidateQueries({ queryKey: ["dm-templates"] });
    void qc.invalidateQueries({ queryKey: ["dm-sends"] });
  };

  const killMut = useMutation({
    mutationFn: (enabled: boolean) => api.dmSetKillSwitch(enabled),
    onSuccess: invalidate,
  });
  const dryMut = useMutation({
    mutationFn: (enabled: boolean) => api.dmSetDryRun(enabled),
    onSuccess: invalidate,
  });
  const enableMut = useMutation({
    mutationFn: ({ id, enabled }: { id: string; enabled: boolean }) =>
      api.dmSetRuleEnabled(id, enabled),
    onSuccess: invalidate,
  });
  const saveRuleMut = useMutation({
    mutationFn: (body: Parameters<typeof api.dmUpsertRule>[0]) => api.dmUpsertRule(body),
    onSuccess: () => {
      setEditRule(null);
      invalidate();
    },
  });
  const saveTplMut = useMutation({
    mutationFn: (body: Parameters<typeof api.dmUpsertTemplate>[0]) => api.dmUpsertTemplate(body),
    onSuccess: () => {
      setEditTemplate(null);
      invalidate();
    },
  });
  const testMut = useMutation({
    mutationFn: () =>
      api.dmTestSend({
        comment_id: testCommentId.trim(),
        text: testText,
        platform: testPlatform,
        post_id: `test_post_${testPlatform}`,
        author_id: "operator_test",
        force_live: false,
      }),
    onSuccess: () => {
      setTestCommentId(`test_${Date.now()}`);
      invalidate();
    },
  });

  const status = statusQ.data;

  return (
    <div>
      <PageHeader
        title="Automatyzacje DM"
        subtitle="Komentarz (hasło) → prywatna wiadomość z przyciskiem URL. Zamiennik ManyChat."
        actions={
          <Button size="sm" variant="outline" onClick={invalidate}>
            <RefreshCw className="mr-2 h-3.5 w-3.5" />
            Odśwież
          </Button>
        }
      />

      <div className="mb-4 grid gap-3 md:grid-cols-4">
        <Card>
          <CardContent className="flex items-center justify-between p-4">
            <div>
              <div className="text-xs text-muted-foreground">Kill-switch</div>
              <div className="text-sm font-medium">
                {status?.kill_switch ? "Wstrzymane" : "Aktywne"}
              </div>
            </div>
            <FlagToggle
              label="Kill-switch"
              checked={!!status?.kill_switch}
              onChange={(v) => killMut.mutate(v)}
            />
          </CardContent>
        </Card>
        <Card>
          <CardContent className="flex items-center justify-between p-4">
            <div>
              <div className="text-xs text-muted-foreground">Dry-run</div>
              <div className="text-sm font-medium">
                {status?.dry_run ? "Bez wysyłki Graph" : "Live Graph"}
              </div>
            </div>
            <FlagToggle
              label="Dry-run"
              checked={!!status?.dry_run}
              onChange={(v) => dryMut.mutate(v)}
            />
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-xs text-muted-foreground">Reguły włączone</div>
            <div className="text-lg font-semibold">
              {status?.rules_enabled ?? "–"} / {status?.rules_total ?? "–"}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-xs text-muted-foreground">Wysyłki / błędy 24h</div>
            <div className="text-lg font-semibold">
              {status?.sends_24h ?? "–"}{" "}
              <span className="text-sm font-normal text-muted-foreground">
                / {status?.errors_24h ?? 0} err
              </span>
            </div>
          </CardContent>
        </Card>
      </div>

      {status?.dry_run && (
        <div className="mb-4 flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
          <ShieldOff className="mt-0.5 h-4 w-4 shrink-0" />
          Dry-run włączony: webhooki i test zapisują log bez wywołania Graph. Przed cutoverem ManyChat
          wyłącz dry-run i włącz reguły produkcyjne.
        </div>
      )}

      <div className="mb-6 grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Reguły (hasła)</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {(rulesQ.data?.items || []).map((rule) => {
              const tpl = templatesById.get(rule.template_id);
              return (
                <div
                  key={rule.id}
                  className="flex flex-col gap-2 rounded-md border border-border/60 p-3 sm:flex-row sm:items-center sm:justify-between"
                >
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-medium">{rule.name}</span>
                      <Badge variant="outline">{rule.platforms}</Badge>
                      <Badge variant="secondary">{rule.match_mode}</Badge>
                    </div>
                    <div className="mt-1 text-xs text-muted-foreground">
                      hasła: {(rule.keywords || []).join(", ") || "–"} · szablon:{" "}
                      {tpl?.name || rule.template_id}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        setEditRule(rule);
                        setKeywordsDraft((rule.keywords || []).join(", "));
                      }}
                    >
                      Edytuj
                    </Button>
                    <FlagToggle
                      label={`Włącz ${rule.name}`}
                      checked={rule.enabled}
                      onChange={(v) => enableMut.mutate({ id: rule.id, enabled: v })}
                    />
                  </div>
                </div>
              );
            })}
            {!rulesQ.data?.items?.length && (
              <p className="text-sm text-muted-foreground">Brak reguł (seed przy starcie API).</p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Szablony DM (tekst + przyciski)</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {(templatesQ.data?.items || []).map((tpl) => (
              <div key={tpl.id} className="rounded-md border border-border/60 p-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="font-medium">{tpl.name}</div>
                    <p className="mt-1 text-xs text-muted-foreground">
                      Na FB: klawisz w bańce DM (button template). Na IG: to samo, a przy
                      odrzuceniu Graph: fallback tekst + URL.
                    </p>
                    {/* Podgląd jak Messenger: tekst + klawisz w bańce */}
                    <div className="mt-3 max-w-sm rounded-2xl border border-border/70 bg-muted/40 p-3 shadow-sm">
                      <p className="whitespace-pre-wrap text-sm leading-relaxed">
                        {tpl.body_text}
                      </p>
                      <div className="mt-3 space-y-2">
                        {(tpl.buttons || []).map((b, i) => (
                          <div
                            key={`${tpl.id}-btn-${i}`}
                            className="rounded-xl border border-border bg-background px-3 py-2 text-center text-sm font-semibold"
                            title={b.url}
                          >
                            {b.title}
                          </div>
                        ))}
                        {!tpl.buttons?.length && (
                          <div className="text-xs text-muted-foreground">Brak przycisku URL</div>
                        )}
                      </div>
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      setEditTemplate(tpl);
                      setButtonsDraft(
                        (tpl.buttons || [])
                          .map((b) => `${b.title} | ${b.url}`)
                          .join("\n"),
                      );
                    }}
                  >
                    Edytuj
                  </Button>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>

      <Card className="mb-6">
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-base">
            <TestTube2 className="h-4 w-4" />
            Test (dry-run)
          </CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-3 sm:flex-row sm:items-end">
          <label className="flex flex-1 flex-col gap-1 text-xs">
            comment_id
            <input
              className="rounded-md border border-border bg-background px-3 py-2 text-sm"
              value={testCommentId}
              onChange={(e) => setTestCommentId(e.target.value)}
            />
          </label>
          <label className="flex w-28 flex-col gap-1 text-xs">
            platform
            <select
              className="rounded-md border border-border bg-background px-3 py-2 text-sm"
              value={testPlatform}
              onChange={(e) => setTestPlatform(e.target.value as "fb" | "ig")}
            >
              <option value="fb">fb</option>
              <option value="ig">ig</option>
            </select>
          </label>
          <label className="flex w-40 flex-col gap-1 text-xs">
            tekst
            <input
              className="rounded-md border border-border bg-background px-3 py-2 text-sm"
              value={testText}
              onChange={(e) => setTestText(e.target.value)}
            />
          </label>
          <Button onClick={() => testMut.mutate()} disabled={testMut.isPending}>
            Uruchom test
          </Button>
        </CardContent>
        {testMut.data && (
          <CardContent className="pt-0">
            <pre className="overflow-x-auto rounded-md bg-muted/50 p-3 text-xs">
              {JSON.stringify(testMut.data, null, 2)}
            </pre>
          </CardContent>
        )}
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-base">
            <MessageSquareWarning className="h-4 w-4" />
            Log wysyłek
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border/50 text-xs uppercase tracking-wider text-muted-foreground">
                <th className="px-4 py-2 text-left font-medium">Czas</th>
                <th className="px-4 py-2 text-left font-medium">Status</th>
                <th className="px-4 py-2 text-left font-medium">Platforma</th>
                <th className="px-4 py-2 text-left font-medium">Hasło</th>
                <th className="px-4 py-2 text-left font-medium">Komentarz</th>
                <th className="px-4 py-2 text-left font-medium">Błąd / mode</th>
              </tr>
            </thead>
            <tbody>
              {(sendsQ.data?.items || []).map((s: DmSend) => (
                <tr key={s.id} className="border-b border-border/30 last:border-0">
                  <td className="px-4 py-2 text-muted-foreground">
                    {s.created_at ? relativeTime(s.created_at) : "–"}
                  </td>
                  <td className="px-4 py-2">
                    <Badge variant={statusBadge(s.status)}>{s.status}</Badge>
                  </td>
                  <td className="px-4 py-2">{s.platform}</td>
                  <td className="px-4 py-2">{s.matched_keyword || "–"}</td>
                  <td className="max-w-[12rem] truncate px-4 py-2" title={s.comment_text}>
                    {s.comment_text || s.comment_id}
                  </td>
                  <td
                    className={cn(
                      "max-w-[14rem] truncate px-4 py-2 text-muted-foreground",
                      s.status === "error" && "text-destructive",
                    )}
                    title={s.error || s.payload_mode}
                  >
                    {s.error || s.payload_mode || s.mode || "–"}
                  </td>
                </tr>
              ))}
              {!sendsQ.data?.items?.length && (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">
                    Brak wysyłek.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {editRule && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <Card className="w-full max-w-lg">
            <CardHeader>
              <CardTitle className="text-base">Edycja reguły</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <label className="flex flex-col gap-1 text-xs">
                Nazwa
                <input
                  className="rounded-md border px-3 py-2 text-sm"
                  value={editRule.name}
                  onChange={(e) => setEditRule({ ...editRule, name: e.target.value })}
                />
              </label>
              <label className="flex flex-col gap-1 text-xs">
                Hasła (po przecinku)
                <input
                  className="rounded-md border px-3 py-2 text-sm"
                  value={keywordsDraft}
                  onChange={(e) => setKeywordsDraft(e.target.value)}
                />
              </label>
              <label className="flex flex-col gap-1 text-xs">
                Platformy
                <select
                  className="rounded-md border px-3 py-2 text-sm"
                  value={editRule.platforms}
                  onChange={(e) => setEditRule({ ...editRule, platforms: e.target.value })}
                >
                  <option value="both">both</option>
                  <option value="fb">fb</option>
                  <option value="ig">ig</option>
                </select>
              </label>
              <label className="flex flex-col gap-1 text-xs">
                Szablon
                <select
                  className="rounded-md border px-3 py-2 text-sm"
                  value={editRule.template_id}
                  onChange={(e) => setEditRule({ ...editRule, template_id: e.target.value })}
                >
                  {(templatesQ.data?.items || []).map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name}
                    </option>
                  ))}
                </select>
              </label>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setEditRule(null)}>
                  Anuluj
                </Button>
                <Button
                  onClick={() =>
                    saveRuleMut.mutate({
                      ...editRule,
                      keywords: keywordsDraft
                        .split(",")
                        .map((x) => x.trim())
                        .filter(Boolean),
                      template_id: editRule.template_id,
                    })
                  }
                >
                  Zapisz
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {editTemplate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <Card className="w-full max-w-lg">
            <CardHeader>
              <CardTitle className="text-base">Edycja szablonu</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <label className="flex flex-col gap-1 text-xs">
                Nazwa
                <input
                  className="rounded-md border px-3 py-2 text-sm"
                  value={editTemplate.name}
                  onChange={(e) => setEditTemplate({ ...editTemplate, name: e.target.value })}
                />
              </label>
              <label className="flex flex-col gap-1 text-xs">
                Treść DM
                <textarea
                  className="min-h-[100px] rounded-md border px-3 py-2 text-sm"
                  value={editTemplate.body_text}
                  onChange={(e) =>
                    setEditTemplate({ ...editTemplate, body_text: e.target.value })
                  }
                />
              </label>
              <label className="flex flex-col gap-1 text-xs">
                Przyciski (linia: tytuł | url). Max 20 znaków tytułu. Na FB klawisz w bańce
                DM. {"{platform}"} → facebook/instagram
                <textarea
                  className="min-h-[80px] rounded-md border px-3 py-2 font-mono text-xs"
                  value={buttonsDraft}
                  onChange={(e) => setButtonsDraft(e.target.value)}
                  placeholder={"SPRAWDZAM | https://kasiazareba.pl/pakiet-ebookow.html?utm_medium=dm"}
                />
              </label>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setEditTemplate(null)}>
                  Anuluj
                </Button>
                <Button
                  onClick={() => {
                    const buttons = buttonsDraft
                      .split("\n")
                      .map((line) => line.trim())
                      .filter(Boolean)
                      .map((line) => {
                        const [title, ...rest] = line.split("|");
                        return {
                          title: (title || "").trim(),
                          url: rest.join("|").trim(),
                        };
                      })
                      .filter((b) => b.title && b.url);
                    saveTplMut.mutate({
                      id: editTemplate.id,
                      name: editTemplate.name,
                      body_text: editTemplate.body_text,
                      buttons,
                    });
                  }}
                >
                  Zapisz
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
